import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowLeft } from 'lucide-react';
import { companyInfo, services } from '../mock';
import AdvancedSEO from '../components/AdvancedSEO';
import SEOHead from '../components/SEOHead';

const ServiceDetail = () => {
  const { id } = useParams();
  const service = services.find(s => s.id === id);

  if (!service) {
    return (
      <div className="pt-32 pb-20 container text-center">
        <h1 className="heading-2 mb-4">Service Not Found</h1>
        <Link to="/services" className="btn-primary">
          View All Services
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { name: "Home", url: "https://nypestexterminating.com" },
    { name: "Services", url: "https://nypestexterminating.com/services" },
    { name: service.title, url: `https://nypestexterminating.com/services/${service.id}` }
  ];

  const servicePageTitle = `${service.title} NYC | Professional ${service.title} | NY Pest & Exterminating`;
  const servicePageDescription = `Professional ${service.title.toLowerCase()} in NYC. Same-day eco-friendly service. Licensed & insured. Serving all 5 boroughs. Call (646) 209-9955 for free quote!`;
  const serviceKeywords = `${service.title.toLowerCase()} NYC, ${service.title.toLowerCase()} New York, professional ${service.title.toLowerCase()}, eco-friendly ${service.title.toLowerCase()}, ${service.title.toLowerCase()} Manhattan, ${service.title.toLowerCase()} Brooklyn, ${service.title.toLowerCase()} Queens, ${service.title.toLowerCase()} Bronx, ${service.title.toLowerCase()} Staten Island, same day ${service.title.toLowerCase()}`;

  const serviceData = {
    name: service.title,
    description: service.description,
    offers: [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        }
      }
    ]
  };

  // Function to parse and format the service description
  const formatServiceDescription = (description) => {
    // Split description into sections based on common patterns
    const sections = description.split(/\n\n(?=[A-Z])/);
    
    return sections.map((section, index) => {
      const lines = section.split('\n').filter(line => line.trim());
      if (lines.length === 0) return null;
      
      const firstLine = lines[0];
      
      // Check if first line is a heading (contains colons, all caps, or specific patterns)
      const isHeading = firstLine.includes(':') || 
                       /^[A-Z\s&]+$/.test(firstLine) || 
                       firstLine.includes('Emergency') || 
                       firstLine.includes('Why NYC') ||
                       firstLine.includes('Our Comprehensive') ||
                       firstLine.includes('Signs You Need') ||
                       firstLine.includes('Our NYC Service') ||
                       firstLine.includes('Targeted Solutions') ||
                       firstLine.includes('FAQs') ||
                       firstLine.includes('Health Risks');
      
      if (isHeading) {
        const heading = firstLine.replace(/:\s*$/, ''); // Remove trailing colon
        const content = lines.slice(1);
        
        return (
          <div key={index} className="mb-12">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)', lineHeight: '1.3' }}>
              {heading}
            </h2>
            {content.map((line, lineIndex) => {
              if (line.startsWith('•') || line.startsWith('·')) {
                // Bullet point
                return (
                  <div key={lineIndex} className="flex items-start gap-4 mb-4">
                    <div className="w-3 h-3 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--accent-primary)' }}></div>
                    <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{line.substring(1).trim()}</p>
                  </div>
                );
              } else if (/^\d+\./.test(line)) {
                // Numbered list
                return (
                  <div key={lineIndex} className="flex items-start gap-4 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0" style={{ background: 'var(--accent-primary)' }}>
                      {line.match(/^(\d+)/)[1]}
                    </div>
                    <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{line.replace(/^\d+\.\s*/, '')}</p>
                  </div>
                );
              } else if (line.includes('Ans:')) {
                // FAQ format
                const [question, answer] = line.split('Ans:');
                return (
                  <div key={lineIndex} className="mb-6 p-6 rounded-xl" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xl font-semibold mb-3" style={{ color: 'var(--accent-primary)' }}>{question.replace(/^•\s*"?|"?\s*$/g, '')}</p>
                    <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{answer.trim()}</p>
                  </div>
                );
              } else if (line.trim()) {
                // Regular paragraph
                return (
                  <p key={lineIndex} className="text-lg leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {line}
                  </p>
                );
              }
              return null;
            })}
          </div>
        );
      } else {
        // No heading, treat as intro paragraph
        return (
          <div key={index} className="mb-10">
            {lines.map((line, lineIndex) => (
              <p key={lineIndex} className="text-xl leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                {line}
              </p>
            ))}
          </div>
        );
      }
    }).filter(Boolean);
  };

  return (
    <div>
      <AdvancedSEO 
        title={servicePageTitle}
        description={servicePageDescription}
        keywords={serviceKeywords}
        url={`https://nypestexterminating.com/services/${service.id}`}
        breadcrumbs={breadcrumbs}
        serviceData={serviceData}
        localBusinessData={{ name: "NY Pest & Exterminating", phone: "(646) 209-9955" }}
      />
      <SEOHead 
        title={servicePageTitle}
        description={servicePageDescription}
        keywords={serviceKeywords}
        url={`https://nypestexterminating.com/services/${service.id}`}
      />
      {/* Breadcrumb */}
      <section className="pt-32 pb-8">
        <div className="container">
          <Link to="/services" className="flex items-center gap-2 body-small" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={16} />
            Back to Services
          </Link>
        </div>
      </section>

      {/* Hero Section */}
      <section className="pb-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl">
            {/* Service Image */}
            <div className="mb-8">
              <img 
                src={service.image} 
                alt={`Professional ${service.title.toLowerCase()} extermination in NYC - NY Pest & Exterminating`}
                title={`${service.title} Extermination - NY Pest & Exterminating`}
                className="w-full rounded-lg shadow-xl"
                style={{ maxHeight: '400px', objectFit: 'cover' }}
              />
            </div>
            
            <h1 className="heading-1 mb-6">{service.title}</h1>
            <div className="formatted-description">
              {formatServiceDescription(service.description)}
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="space-y-8">
                <div>
                  <h2 className="heading-2 mb-4">What We Offer</h2>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0, marginTop: '2px' }} />
                        <span className="body-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h2 className="heading-2 mb-4">Our Process</h2>
                  <div className="space-y-4">
                    <div className="product-card">
                      <h3 className="heading-3 mb-2">1. Thorough Inspection</h3>
                      <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        Our certified technicians conduct a comprehensive assessment of your property to identify the extent of the infestation and entry points.
                      </p>
                    </div>
                    
                    <div className="product-card">
                      <h3 className="heading-3 mb-2">2. Customized Treatment Plan</h3>
                      <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        We develop a tailored solution using our eco-friendly Green Shield Approach, designed specifically for your situation.
                      </p>
                    </div>
                    
                    <div className="product-card">
                      <h3 className="heading-3 mb-2">3. Safe Application</h3>
                      <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        Our trained professionals apply treatments safely and efficiently, with minimal disruption to your daily routine.
                      </p>
                    </div>
                    
                    <div className="product-card">
                      <h3 className="heading-3 mb-2">4. Follow-Up & Prevention</h3>
                      <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        We schedule follow-up visits to ensure complete elimination and provide prevention strategies to keep pests away long-term.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h2 className="heading-2 mb-4">Why Our Eco-Friendly Approach Works</h2>
                  <p className="body-medium mb-4" style={{ color: 'var(--text-body)' }}>
                    Our Green Shield Approach combines Integrated Pest Management (IPM) principles with the latest eco-friendly technologies. This means we prioritize non-toxic solutions, mechanical exclusion, and habitat modification before resorting to chemical treatments.
                  </p>
                  <p className="body-medium" style={{ color: 'var(--text-body)' }}>
                    The result? Effective pest control that's completely safe for your family, pets, and the environment - without compromising on results.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="product-card sticky top-24">
                <h3 className="heading-3 mb-4">Get Started Today</h3>
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Contact us for a free quote and same-day service availability.
                </p>
                
                <div className="space-y-3 mb-6">
                  <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary w-full">
                    <Phone size={20} />
                    Call Now
                  </a>
                  <Link to="/contact" className="btn-secondary w-full">
                    Request Quote
                  </Link>
                </div>
                
                <div className="pt-6 border-t" style={{ borderColor: 'var(--border-light)' }}>
                  <h4 className="heading-3 mb-3" style={{ fontSize: '1rem' }}>Service Includes:</h4>
                  <ul className="space-y-2">
                    <li className="body-small">✓ Free inspection</li>
                    <li className="body-small">✓ Eco-friendly treatment</li>
                    <li className="body-small">✓ Same-day service</li>
                    <li className="body-small">✓ Follow-up visits</li>
                    <li className="body-small">✓ 100% guarantee</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-2 mb-8 text-center">Other Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.filter(s => s.id !== id).slice(0, 3).map(relatedService => (
              <Link
                key={relatedService.id}
                to={`/services/${relatedService.id}`}
                className="product-card card-hover-lift"
              >
                <h3 className="heading-3 mb-2">{relatedService.title}</h3>
                <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {relatedService.description}
                </p>
                <div className="flex items-center gap-2" style={{ color: 'var(--accent-text)' }}>
                  <span className="body-small font-semibold">Learn More →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;